import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { FormattedMessage, injectIntl } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {
  Button,
  Dialog,
  Typography,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import { Input, Checkbox } from 'components/FormInputs';
import CircularProgress from 'components/shared/CircularProgress';

const useStyles = makeStyles((theme) => ({
  dialog: {
    padding: theme.spacing(2),
    paddingTop: 0,
  },
  title: {
    padding: 0,
    textAlign: 'right',
  },
  paper: {
    padding: theme.spacing(1),
  },
  button: {
    textAlign: 'right',
    textTransform: 'uppercase',
    fontSize: '0.6rem',

    '& a': {
      color: 'red',
    },
  },
}));

const RenderForm = ({
  values,
  handleChange,
  handleSubmit,
  isSubmitting,
  isValid,
  errors,
}) => {
  return (
    <form onSubmit={handleSubmit}>
      <Checkbox
        value={values.confirm}
        name="confirm"
        errors={errors}
        onChange={handleChange}
        label={(
          <FormattedMessage
            id="deleteAccount.understand"
            defaultMessage="I understand, delete my account"
          />
        )}
      />

      {
        values.confirm && (
          <>
            <Input
              label={(
                <FormattedMessage
                  id="deleteAccount.confirmation"
                  defaultMessage="Type 'delete account'"
                />
              )}
              name="confirmation"
              value={values.confirmation}
              errors={errors}
              onChange={handleChange}
            />
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              fullWidth
              disabled={isSubmitting || !isValid}
            >
              <FormattedMessage id="Delete my account" defaultMessage="Delete my account" />
            </Button>
          </>
        )
      }
    </form>
  );
};

RenderForm.propTypes = {
  values: PropTypes.shape({
    confirm: PropTypes.bool,
    confirmation: PropTypes.string,
  }).isRequired,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isValid: PropTypes.bool.isRequired,
  errors: PropTypes.shape({
    confirm: PropTypes.string,
    confirmation: PropTypes.string,
  }).isRequired,
};

const GetDialogContent = ({
  cottages, intl, onSubmit, success,
}) => {
  if (success && success.logout) {
    return (
      <Alert severity="success" elevation={6}>
        <FormattedMessage
          id="deleteAccount.success"
          defaultMessage="Your account has been deleted. You will be redirected to the start page"
        />
      </Alert>
    );
  }

  if (cottages && cottages.length) {
    return (
      <Typography>
        <FormattedMessage
          id="deleteAccount.cottages"
          defaultMessage="Your account contains ads. You must delete those before you can proceed with account deletion."
        />
      </Typography>
    );
  }

  return (
    <>
      <Typography>
        <FormattedMessage
          id="deleteAccount.explanation"
          defaultMessage="Deleting your account is a permanent action. All your information will be lost and you won't be able to log in again."
        />
      </Typography>
      <Formik
        initialValues={{
          confirm: false,
          confirmation: '',
        }}
        validateOnChange
        validate={(values) => {
          const err = {};

          if (!values.confirm) {
            err.confirm = intl.formatMessage({
              id: 'Required',
              defaultMessage: 'Required',
            });
          }

          if (!values.confirmation) {
            err.confirmation = intl.formatMessage({
              id: 'Required',
              defaultMessage: 'Required',
            });
          } else if (values.confirmation !== 'delete account' && values.confirmation !== 'ta bort mitt konto permanent') {
            err.confirmation = intl.formatMessage({
              id: 'deleteAccount.validate.confirmation',
              defaultMessage: 'Write "delete account" to confirm deletion',
            });
          }

          return err;
        }}
        enableReinitialize
        onSubmit={() => onSubmit()}
        render={(fp) => <RenderForm {...fp} />}
      />
    </>
  );
};

GetDialogContent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  cottages: PropTypes.arrayOf(PropTypes.shape({})),
  success: PropTypes.shape({
    logout: PropTypes.string,
  }),
};

const DeleteAccount = (props) => {
  const {
    opened, setOpened, setClosed, onSubmit, isLoading, intl, cottages,
    success,
  } = props;
  const classes = useStyles();

  return (
    <>
      {
        isLoading && <CircularProgress fullHeight />
      }
      <span className={classes.button}>
        <a
          onClick={setOpened}
          role="none"
        >
          <FormattedMessage
            id="Delete my account"
            defaultMessage="Delete my account"
          />
        </a>
      </span>
      {opened && (
        <Dialog
          onClose={setClosed}
          open={opened}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle disableTypography className={classes.title}>
            <IconButton
              aria-label="close"
              className={classes.closeButton}
              onClick={setClosed}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className={classes.dialog}>
            <GetDialogContent
              {...{
                cottages, intl, onSubmit, success,
              }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

DeleteAccount.propTypes = {
  opened: PropTypes.bool.isRequired,
  setOpened: PropTypes.func.isRequired,
  setClosed: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }).isRequired,
  cottages: PropTypes.arrayOf(PropTypes.shape({})),
  success: PropTypes.shape({
    logout: PropTypes.string,
  }),
};

export default injectIntl(DeleteAccount);
